import {
  QueryClient,
  useMutation,
  useQueryClient
} from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { BehApiRoutes, behClient } from '../beh-api';
import { useNotification } from '../../shared/notification/useNotification';
import { ErrorType } from '../../shared/utils/Error/ErrorCode';

function invalidateCaseQueries(
  queryClient: QueryClient,
  caseId: string,
  options?: { includeAllCases?: boolean }
) {
  queryClient.invalidateQueries({
    predicate: (key) =>
      key.queryKey.some(
        (k) =>
          typeof k === 'string' &&
          (k.startsWith(BehApiRoutes.case(caseId)) ||
            (options?.includeAllCases && k.startsWith(BehApiRoutes.allCases())))
      )
  });
}

export function useCancelVersand(caseId: string) {
  const { notifyUserFromRequest, notifyUser } = useNotification();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: (submissionId: string) =>
      notifyUserFromRequest(
        () => behClient.post(BehApiRoutes.versandCancel(caseId, submissionId)),
        {
          successMessage: 'Versandauftrag wurde abgebrochen',
          errorMessage: 'Versandauftrag konnte nicht abgebrochen werden',
          errorIdHandler: {
            [ErrorType.BEHOERDENCLIENT_PROBLEMS_INVALID_REQUEST]: () => {
              notifyUser({
                intent: 'error',
                title: t('api.error.versandStatusOutdated')
              });
              invalidateCaseQueries(queryClient, caseId);
            }
          }
        }
      ),
    onSuccess: () => {
      invalidateCaseQueries(queryClient, caseId);
    }
  });

  return { cancelVersand: mutateAsync, isCanceling: isPending };
}

export function useRetryVersand(caseId: string) {
  const { notifyUserFromRequest, notifyUser } = useNotification();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: (submissionId: string) =>
      notifyUserFromRequest(
        () => behClient.post(BehApiRoutes.versandRetry(caseId, submissionId)),
        {
          successMessage: 'Versand wird erneut durchgeführt',
          errorMessage: 'Versandauftrag konnte nicht erneut gestartet werden',
          errorIdHandler: {
            [ErrorType.BEHOERDENCLIENT_PROBLEMS_INVALID_REQUEST]: () => {
              notifyUser({
                intent: 'error',
                title: t('api.error.versandStatusOutdated')
              });
              invalidateCaseQueries(queryClient, caseId);
            }
          }
        }
      ),
    onSuccess: () => {
      invalidateCaseQueries(queryClient, caseId);
    }
  });

  return { retryVersand: mutateAsync, isRetrying: isPending };
}

export function useSwitchToPostalVersand(caseId: string) {
  const { notifyUserFromRequest, notifyUser } = useNotification();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: (submissionId: string) =>
      notifyUserFromRequest(
        () => behClient.post(BehApiRoutes.versandPostal(caseId, submissionId)),
        {
          successMessage: t('api.success.switchToPostalVersand'),
          errorMessage: t('api.error.switchToPostalVersand'),
          errorIdHandler: {
            [ErrorType.BEHOERDENCLIENT_PROBLEMS_INVALID_REQUEST]: () => {
              notifyUser({
                intent: 'error',
                title: t('api.error.versandStatusOutdated')
              });
              invalidateCaseQueries(queryClient, caseId);
            }
          }
        }
      ),
    onSuccess: () => {
      invalidateCaseQueries(queryClient, caseId);
    }
  });

  return { switchToPostalVersand: mutateAsync, isSwitchingToPostal: isPending };
}

export function useDeleteVersand(caseId: string) {
  const { notifyUserFromRequest, notifyUser } = useNotification();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: (submissionId: string) =>
      notifyUserFromRequest(
        () =>
          behClient.delete(BehApiRoutes.versandDelete(caseId, submissionId)),
        {
          successMessage: 'Nachricht wurde gelöscht',
          errorMessage: 'Nachricht konnte nicht gelöscht werden',
          errorIdHandler: {
            [ErrorType.BEHOERDENCLIENT_PROBLEMS_INVALID_REQUEST]: () => {
              notifyUser({
                intent: 'error',
                title: t('api.error.versandStatusOutdated')
              });
              invalidateCaseQueries(queryClient, caseId, {
                includeAllCases: true
              });
            }
          }
        }
      ),
    onSuccess: () => {
      invalidateCaseQueries(queryClient, caseId, { includeAllCases: true });
    }
  });

  return { deleteVersand: mutateAsync, isDeleting: isPending };
}
